<template>
  <div class="i-home">
    <div class="left">
      <div class="cell"><iUserInformation /></div>
      <div class="cell"><iProfessionalBio /></div>
    </div>
    <div class="right">
      <div class="column">
        <div class="cell"><iPersonalityType /></div>
        <div
          v-if="!isXLargeScreen && jobs"
          class="cell"
        >
          <iProfessionsList
            :jobs="jobs"
            @job-page-redirect="(jobId: number) => pageRedirect(REPORT_ROUTES.careers.routeName, jobId)"
            @upgrade-clicked="emit('open-modal-payment')"
          />
        </div>
        <div class="cell"><iSummaryVideo /></div>
      </div>
      <div class="column">
        <div
          v-if="isXLargeScreen && jobs"
          class="cell"
        >
          <iProfessionsList
            :jobs="jobs"
            @job-page-redirect="(jobId: number) => pageRedirect(REPORT_ROUTES.careers.routeName, jobId)"
            @upgrade-clicked="emit('open-modal-payment')"
          />
        </div>
        <div class="cell"><iIncomePotentialChart /></div>
        <div
          v-if="!isXLargeScreen"
          class="cell"
        >
          <iGoalsAspirations />
          <iCoachingUpsell v-if="!userStore.isTierPremium && !userStore.isActiveReportSubscriber" />
        </div>
      </div>
    </div>

    <!-- Group Coaching Modal -->
    <iGroupCoachingModal
      v-if="groupCoachingVisibility"
      :visible="groupCoachingVisibility"
      @close="closeGroupCoachingModal"
    />
  </div>
</template>

<script setup lang="ts">
import { useReportStore } from "@/stores/report"
import { useUserStore } from "@/stores/user"
import { computed, onMounted, ref } from "vue"
import { useRouter } from "vue-router"

import { breakpointXl } from "@/styles/variables"

import scrollToTop from "@/composables/scrollToTop"
import useIsScreenSize from "@/composables/useIsScreenSize"
import { GlobalVariables } from "@/helpers/globalVariables"

import iCoachingUpsell from "@/components/report/home/CoachingUpsell.vue"
import iGoalsAspirations from "@/components/report/home/GoalsAspirations.vue"
import iGroupCoachingModal from "@/components/report/home/GroupCoachingModal.vue"
import iIncomePotentialChart from "@/components/report/home/IncomePotentialChart.vue"
import iPersonalityType from "@/components/report/home/PersonalityType.vue"
import iProfessionalBio from "@/components/report/home/ProfessionalBio.vue"
import iProfessionsList from "@/components/report/home/ProfessionsList.vue"
import iSummaryVideo from "@/components/report/home/SummaryVideo.vue"
import iUserInformation from "@/components/report/home/UserInformation.vue"

// ENITS
const emit = defineEmits(["open-modal-payment"])

// STORES AND COMPOSABLES
const reportStore = useReportStore()
const userStore = useUserStore()
const isXLargeScreen = useIsScreenSize(breakpointXl)

// ROUTER
const router = useRouter()

// CONSTANTS
const REPORT_ROUTES = GlobalVariables.urls.report.children
const GROUP_COACHING_MODAL_KEY = "group-coaching-modal-seen"
const SUBSCRIPTION_BUNDLE_MODAL_KEY = "subscription-bundle-modal-seen"

// REACTIVE VARIABLES

/** Whether the group coaching modal should be shown */
const groupCoachingVisibility = ref(false)

// COMPUTED
const jobs = computed(() => {
  const recommendedJobs = reportStore.getRecommendedJobs
  if (!recommendedJobs) return null
  return recommendedJobs.length
    ? userStore.isTierAnyPaid
      ? recommendedJobs
          .filter((job) => job.isVisible && !job.isCurrentJob && !job.isActivelyExploringJob && !job.isSampleJob)
          .slice(0, 5)
      : recommendedJobs.filter((job) => job.isVisible && !job.isSampleJob)
    : null
})

// FUNCTIONS
const pageRedirect = (routeName: string, jobId?: number) => {
  if (jobId) {
    router.push({ name: routeName, params: { jobId } })
  } else {
    router.push({ name: routeName })
  }
  scrollToTop()
}

/**
 * Checks if the user is a report subscriber, and whether they have seen the group coaching modal.
 * If not, it sets the modal to be visible.
 */
const setGroupCoachingVisibility = () => {
  const reportSubscriber = userStore.isActiveReportSubscriber
  const coachingPaid = userStore.isCoachingPaiduser
  const thisModalSeen = localStorage.getItem(GROUP_COACHING_MODAL_KEY)
  const subscriptionBundleModalSeen = localStorage.getItem(SUBSCRIPTION_BUNDLE_MODAL_KEY)
  if (reportSubscriber && !thisModalSeen && (!coachingPaid || subscriptionBundleModalSeen)) {
    groupCoachingVisibility.value = true
  }
}

/**
 * Closes the group coaching modal and sets it as seen.
 */
const closeGroupCoachingModal = () => {
  groupCoachingVisibility.value = false
  localStorage.setItem(GROUP_COACHING_MODAL_KEY, "true")
}

// LIFECYCLE
onMounted(() => {
  setGroupCoachingVisibility()
})
</script>

<style scoped lang="scss">
.i-home {
  position: relative;

  @media screen and (min-width: $breakpoint-lg) {
    height: calc(100vh - 80px);
    min-height: 743px;
    max-height: 1000px;
    display: grid;
    grid-template-columns: 300px 1fr;
    overflow-y: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .left {
    @media screen and (min-width: $breakpoint-lg) {
      height: calc(100vh - 80px);
      min-height: 743px;
      max-height: 1000px;
      position: sticky;
      top: 0;

      .cell {
        height: 50%;
      }
    }
  }

  .right {
    @media screen and (min-width: $breakpoint-xl) {
      height: 100%;
      display: grid;
      grid-template-columns: 1fr 1fr;

      .cell {
        height: 50%;
      }
    }
  }
}
</style>
